import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { Grid, Tooltip, Typography } from '@mui/material';
import {
  Cell,
  Label,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

import useFetchData from '../../../hooks/useFetchData';
import { toggleOpeningAnimation } from '../../../redux/pages/actions';
import GeneralComponentErrorShield from '../../general/GeneralComponentErrorShield';
import {
  clientConfigurationSelector,
  openingAnimationIsActiveSelector,
} from '../../../redux/pages/selectors';
import { clientNameSelector } from '../../../redux/auth/selectors';
import GridItem from '../../layout/GridComponents/GridItem';
import { addCommasToNumbersAndRound } from '../../../utilities/numberFormatters';
import { greys, mainColors, RaptorTheme } from '../../../styling/theme';
import { mapStatusToColor } from '../../../utilities/colorUtilities';
import { DataObject } from '../../../types/redux/data/dataTypes';
import { Theme } from '@emotion/react';
import { currency_symbols_map } from '../../../utilities/generalMappings';
import { raptorPlatformIdSelector } from '../../../redux/raptorPlatform/selectors';

// Main Dashboard Component
const LegacyDashboard: FC = () => {
  const clientConfiguration = useSelector(clientConfigurationSelector);
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  const data = new FormData();
  data.append('sections', JSON.stringify(clientConfiguration?.sections));

  const shouldMakeFetch = !!(
    clientConfiguration?.configId && clientConfiguration?.sections
  );

  const url = raptorPlatformId
    ? `/dashboard_data?raptor_platform_id=${raptorPlatformId}`
    : `/dashboard_data`;

  const DashboardData = useFetchData({
    url,
    keyName: `dashboard_data_legacy ${raptorPlatformId}`,
    makeFetch: shouldMakeFetch,
    formData: data,
  });

  return (
    <GeneralComponentErrorShield
      dataObjects={[DashboardData]}
      customLoadingMessages={['Loading Dashboard Data...']}
      customErrorMessage="No Data Available."
    >
      <Grid container spacing={2} style={{ padding: 8 }}>
        <DashboardContainers inputData={DashboardData} />
      </Grid>
    </GeneralComponentErrorShield>
  );
};

export default LegacyDashboard;

// Dashboard Containers Component
interface DashboardContainersProps {
  inputData: DataObject;
}

const useDashboardContainersStyles = makeStyles<Theme>(() => ({
  root: {
    transition: 'all .3s',
    '&:hover': {
      transform: 'scale(0.99)',
    },
  },
}));

const DashboardContainers: React.FC<DashboardContainersProps> = ({
  inputData,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    setTimeout(() => {
      dispatch(toggleOpeningAnimation());
    }, 6000);
  }, []);

  const classes = useDashboardContainersStyles();

  return (
    <>
      {(Array.isArray(inputData?.data) && inputData.data.length > 0
        ? inputData.data[0].dashboard_containers
        : []
      )
        // Filter out empty regulation containers (no funds and no aum)
        .filter((container: any) => {
          if (container?.container_type !== 'regulation') return true;
          const funds = Number(container?.data?.overview_data?.funds ?? 0);
          const aum = Number(container?.data?.overview_data?.aum ?? 0);
          return funds > 0 || aum > 0;
        })
        .map((container: any, index: number) => (
          <GridItem
            card
            key={index}
            classes={classes}
            cardStyle={{ height: '18.75rem' }}
            xs={12}
          >
            {container.container_type === 'regulation' ? (
              <RegulationContainer
                sectionId={container.section_id}
                sectionTitle={container.section_title}
                data={container.data}
                delayIndex={index}
                currencyCode={inputData.data[0].manco_base_currency}
              />
            ) : null}
            {/* Here we allow for expandability with different types of dashboard containers... */}
          </GridItem>
        ))}
    </>
  );
};

// Regulation Container Component
interface RegulationContainerProps {
  sectionId: string;
  sectionTitle: string;
  data: any;
  delayIndex: number;
  currencyCode?: string;
}

const useRegulationContainerStyles = makeStyles((theme: RaptorTheme) => ({
  regulationContainerRoot: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0.62rem 1.88rem',
    paddingRight: 0,
    transition: 'all .3s',
    height: '100%',
    width: '100%',
    zIndex: 1,
    position: 'relative',
    cursor: 'pointer',
  },
  textSection: {
    display: 'flex',
    borderRadius: 4,
    flexDirection: 'column',
    justifyContent: 'space-around',
    width: '25%',
  },
  subTitle: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
  },
  subTitle_info: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
    fontSize: '1.50rem',
    fontWeight: 'bold',
    marginBottom: '0.94rem',
    color: theme.palette.primary.main,
  },
  pieSection: {
    cursor: 'pointer',
    display: 'flex',
    borderRadius: 4,
    width: '75%',
    justifyContent: 'space-around',
  },
}));

const RegulationContainer: FC<RegulationContainerProps> = ({
  sectionId,
  sectionTitle,
  data,
  delayIndex,
  currencyCode,
}) => {
  const classes = useRegulationContainerStyles();
  const navigate = useNavigate();
  const clientName = useSelector(clientNameSelector);

  const handleClick = () => {
    // This is to handle legacy version of AIFMD code for Quayside and WCapital
    // Quayside and WCapital are the only clients using this, all future AIFMD clients will use the new version (eg. bflexion)

    // Handle the legacy aifmd page for quayside and wcapital
    if (
      sectionId === 'aifmd' &&
      (clientName === 'quayside' || clientName === 'wcapital')
    ) {
      navigate(`/${sectionId}/${sectionId}-overview-legacy`);
    } else if (sectionId === 'pe' || sectionId === 'aifmd') {
      navigate(`/${sectionId}/${sectionId}-overview`);
    } else {
      navigate(`/${sectionId}/overview`);
    }
  };

  return (
    <div className={classes.regulationContainerRoot} onClick={handleClick}>
      <div className={classes.textSection}>
        <Typography variant="h1">{sectionTitle}</Typography>
        <Typography variant="h2" className={classes.subTitle}>
          Number of Sub-Funds:
        </Typography>
        <Typography variant="h3" className={classes.subTitle_info}>
          {data.overview_data.funds}
        </Typography>
        <Typography variant="h2" className={classes.subTitle}>
          Assets Under Management:
        </Typography>
        <Typography variant="h3" className={classes.subTitle_info}>
          {(currencyCode ? currency_symbols_map[currencyCode] : '€') +
            ' ' +
            addCommasToNumbersAndRound(data.overview_data.aum)}
        </Typography>
      </div>
      <div className={classes.pieSection}>
        <PieContainer
          sectionId={sectionId}
          sectionTitle={sectionTitle}
          data={data.pies_data}
          delayIndex={delayIndex}
        />
      </div>
    </div>
  );
};

// Pie Container Component
interface PieContainerProps {
  data: any;
  delayIndex: number;
  sectionId: string;
  sectionTitle: string;
}

const usePieContainerStyles = makeStyles(() => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
  },
}));

const mapPieToRoute = (section: string, pie: string): string | null => {
  // any special cases go here (hopefully over time we can remove these)
  if (section.toUpperCase() === pie) {
    return `/${section}/${section}-law`;
  } else if (section == 'pe' && pie == 'Commitment') {
    return `/${section}/pe-exposure`;
  }
  // for bflexion / aif weirdness
  if (section === 'aifmd' && pie === 'Value at Risk') {
    return `/${section}/exposure`;
  }

  // else standard page selections
  switch (pie) {
    case 'Commitment':
      return `/${section}/exposure`;
    case 'Value at Risk':
      return `/${section}/value-at-risk`;
    case 'Prospectus':
      return `/${section}/prospectus-restrictions`;
    case 'CSSF Risk Spreading':
      return `/${section}/cssf-risk-spreading`;
    case 'Leverage':
      return `/${section}/exposure`;
    case 'Supplementary Rules':
      return `/${section}/supplementary-rules`;
    default:
      return null;
  }
};

const PieContainer: FC<PieContainerProps> = ({
  data,
  delayIndex,
  sectionId,
  sectionTitle,
}) => {
  const classes = usePieContainerStyles();

  return (
    <div className={classes.container}>
      {Object.keys(data).map((item: any, index: number) => {
        const pieRoute = mapPieToRoute(sectionId, item);

        // Sum the data values to ensure that they are greater than 0, if not then do not create chart
        const dataSum = data[item]
          .slice(1)
          .reduce(
            (acucumulator: number, currentValue: Array<string | number>) =>
              acucumulator + Number(currentValue[1]),
            0,
          );

        const shouldShowPie =
          dataSum > 0 || item === 'Supplementary Rules';

        return shouldShowPie ? (
          <IndividualPie
            pieRoute={pieRoute}
            animBegin={(index + 1) * 300 + 1200 * delayIndex}
            key={index}
            dataTitle={item}
            data={data[item]}
          />
        ) : null;
      })}
    </div>
  );
};

// Individual Pie Component
interface IndividualPieProps {
  animBegin: number;
  dataTitle: string;
  data: any;
  pieRoute: string | null;
}

const useIndividualPieStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: 'white',
    transition: 'all .3s',
    height: 'fit-content',
    padding: '0.62rem',
    '&:hover': {
      backgroundColor: greys.grey200,
    },
    borderRadius: 16,
  },
  chartContainer: {
    width: 160,
    height: 160,
    flexShrink: 0,
  },
  title: {
    fontSize: '1.25rem',
    width: '100%',
    minHeight: '3.75rem',
    lineHeight: 1.5,
    fontWeight: 500,
    color: mainColors.mainBlue,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    textAlign: 'center',
  },
  colorPoint: {
    height: 8,
    width: 8,
    borderRadius: '50%',
    marginRight: '0.62rem',
    '&.pass': {
      background: 'rgba(19,187,57,1)',
    },
    '&.alert': {
      background: 'rgba(255,115,21,1)',
    },
    '&.fail': {
      background: 'rgba(236,14,14,1)',
    },
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  displayPie: {
    position: 'absolute',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  label: {
    fontSize: '1.25rem',
    color: mainColors.mainBlue,
    fontWeight: 500,
  },
  labelNA: {
    fontSize: '1.25rem',
    color: mainColors.NA,
    fontWeight: 500,
  },
}));

const IndividualPie: FC<IndividualPieProps> = ({
  animBegin,
  dataTitle,
  data,
  pieRoute,
}) => {
  const navigate = useNavigate();
  const classes = useIndividualPieStyles();
  const animationsActive = useSelector(openingAnimationIsActiveSelector);

  const handlePieClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (pieRoute) navigate(pieRoute);
  };

  const newData = data.slice(1).map((datum: any) => {
    return { name: datum[0], value: Number(datum[1]) };
  });

  const total = newData.reduce((vals: any, currentVal: any) => {
    return vals + currentVal.value;
  }, 0);

  const passValue =
    newData.find((item: any) => item.name === 'Pass')?.value ?? 0;

  const isNA = total === 0;
  const pc_good = total > 0 ? passValue / total : 0;
  const pieData = isNA ? [{ name: 'N/A', value: 1 }] : newData;

  return (
    <Tooltip title={`Access ${dataTitle} Data`}>
      <div className={classes.root} onClick={handlePieClick}>
        <div className={classes.chartContainer}>
          <ResponsiveContainer width={160} height={160}>
          <PieChart>
            <RechartsTooltip
              itemStyle={{ fontSize: '1.00rem', color: mainColors.mainBlue }}
              formatter={(value: any, name: any, props: any) =>
                isNA ? 'N/A' : value + ' funds'
              }
            />
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={'80%'}
              innerRadius={'50%'}
              fill={mainColors.NA}
              animationBegin={animBegin}
              isAnimationActive={animationsActive && !isNA}
              animationDuration={1000}
              cursor={'pointer'}
            >
              {pieData.map((entry: any, index: any) => {
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={isNA ? mainColors.NA : mapStatusToColor(entry.name)}
                  />
                );
              })}
              <Label
                width={30}
                position="center"
                className={isNA ? classes.labelNA : classes.label}
              >
                {isNA ? 'N/A' : Math.round(pc_good * 100) + '%'}
              </Label>
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        </div>

        <Typography variant="h2" align="center" className={classes.title}>
          {dataTitle}
        </Typography>
      </div>
    </Tooltip>
  );
};
